import type {RuleSeverity} from '@github-ui/code-scanning-shared/types/rule-severity'
import type {Finding} from './types'

/** Display order for severity levels (most severe first) */
export const SEVERITY_ORDER: RuleSeverity[] = [
  'failure',
  'critical',
  'high',
  'error',
  'medium',
  'warning',
  'low',
  'note',
  'nit',
  'notice',
  'none',
]

const getSeveritySortIndex = (severity: RuleSeverity | undefined): number => {
  if (!severity) {
    return SEVERITY_ORDER.length
  }

  const index = SEVERITY_ORDER.indexOf(severity)
  return index === -1 ? SEVERITY_ORDER.length : index
}

const SEVERITY_MAP: Record<string, RuleSeverity> = {
  critical: 'critical',
  high: 'high',
  moderate: 'medium',
  low: 'low',
}

const SOURCE_LABELS: Record<string, string> = {
  check_run: 'Actions',
  code_scanning: 'CodeQL',
  pr_review: 'Copilot',
}

export const getFindingSeverity = (finding: Finding): RuleSeverity | undefined => {
  return SEVERITY_MAP[finding.severity]
}

export const getFindingSource = (finding: Finding): string => {
  return SOURCE_LABELS[finding.source] ?? finding.source
}

export const getFindingFileLocation = (finding: Finding): string | null => {
  if (!finding.filePath) return null
  return finding.startLine === finding.endLine
    ? `${finding.filePath}:${finding.startLine}`
    : `${finding.filePath}:${finding.startLine}-${finding.endLine}`
}

export const getFindingTitle = (finding: Finding): string => {
  const msg = finding.group
  if (msg.length <= 100) return msg
  const truncated = msg.slice(0, 100)
  const lastSpace = truncated.lastIndexOf(' ')
  return lastSpace > 0 ? `${truncated.slice(0, lastSpace)}…` : `${truncated}…`
}

export const sortFindingsBySeverity = (findings: Finding[]): Finding[] => {
  return [...findings].sort((a, b) => {
    const indexA = getSeveritySortIndex(getFindingSeverity(a))
    const indexB = getSeveritySortIndex(getFindingSeverity(b))
    return indexA - indexB
  })
}

const HIGH_ATTENTION_SEVERITIES: ReadonlySet<RuleSeverity> = new Set(['critical', 'high'])

export const partitionFindingsByAttention = (findings: Finding[]): [Finding[], Finding[]] => {
  const attention: Finding[] = []
  const other: Finding[] = []

  for (const f of findings) {
    const severity = getFindingSeverity(f)
    if (severity && HIGH_ATTENTION_SEVERITIES.has(severity)) {
      attention.push(f)
    } else {
      other.push(f)
    }
  }
  return [sortFindingsBySeverity(attention), sortFindingsBySeverity(other)]
}
